import React from 'react';
import PropTypes from 'prop-types';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import Tabs from '../../utility/tab/Tabs';
import { Calculator, Annotations, Accessibility } from '../ToolkitIcons';
import { Bookmark, Expand, ZoomIcon } from '../Svgs';
import A11y from 'chameleon/ui-stack/utilities/a11y';

import './styles.scss';

const HelpMenuToolContent = ({ label, description }) => (
  <div>
    <A11y>
      <div className="HelpMenu--tab-label">{label}</div>
    </A11y>
    <A11y>
      <div className="HelpMenu--tab-content">{description}</div>
    </A11y>
  </div>
);

HelpMenuToolContent.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const HelpMenuTools = ({ hasCalculator }) => {
  const tabs = [
    {
      icon: <Annotations disableHover />,
      label: 'Annotation',
      description: `
      To add highlighting or notes, click the Annotation tool and use
      your cursor to highlight text. Then, select one of the four colors
      to add highlighting. To add a sticky note to the highlighted text,
      click the sticky note icon. Clicking that icon will show a popup
      window where you can write notes for yourself. After you click ‘Add Note’,
      the sticky note will appear next to the text. To remove highlighting and
      sticky notes, select the highlighted text and click 'Remove Highlight and Sticky Note'.
      `,
    },
    {
      icon: <Accessibility disableHover />,
      label: 'Accessibility',
      description: `
      Navigate to the Accessibility menu to change the exam player's
      color scheme or font size.
      `,
    },
    {
      icon: <Bookmark />,
      label: 'Bookmarking',
      description: `
      You can bookmark a question to remind yourself to come back to
      it later. To bookmark a question, click the bookmark icon.
      After you do so, a ribbon will appear above the question circle.
      To remove the ribbon, click the bookmark icon again.
      `,
    },
    {
      icon: <Expand />,
      label: 'Expand Passage',
      description: `
      Click the 'Expand' button to hide answer options and expand a
      passage across the screen. Click the 'Collapse' button to show
      answer options and collapse a passage back into a separate column.
      `,
    },
    {
      icon: <ZoomIcon />,
      label: 'Zoom',
      description: `
      To zoom into the exam player, click the magnifying glass at the
      bottom of the exam player. Click the -/+, or drag the slider, to
      zoom in or out. Click the >> to collapse the zoom bar.
      `,
    },
  ];

  if (hasCalculator) {
    tabs.unshift({
      icon: <Calculator disableHover />,
      label: 'Calculator',
      description: `
      Click the calculator icon to open and use the Desmos calculator.
      To close the calculator, simply click outside of the calculator.
      For more information about the Desmos calculator, click the second
      tab called "Calculator" in this help menu.
      `,
    });
  }

  return (
    <div className="HelpMenu--tools">
      <div className="HelpMenu--tools-header">
        Click an icon to learn more about the feature.
      </div>
      <Tabs
        tabItemsClassname="HelpMenu--tools--Tabs"
        tabClassname="HelpMenu--tools--Tab"
        activeClassname="HelpMenu--tools--Tab--active"
      >
        {tabs.map((details, index) => (
          <div
            index={index}
            key={index}
            icon={details.icon}
            aria-label={details.label}
          >
            <HelpMenuToolContent {...details} />
          </div>
        ))}
      </Tabs>
    </div>
  );
};

const calculatorVideos = {
  graphing: 'https://www.youtube.com/embed/QfjoWg0u4HQ',
  scientific: 'https://www.youtube.com/embed/bFYeJd4_TAM',
};

const HelpMenu = ({ onClose, calculatorType }) => (
  <ConfirmDialog wrapperClassName="HelpMenu" title="Help" onClose={onClose}>
    <div className="HelpMenu--body">
      <Tabs
        tabItemsClassname="HelpMenu--body--Tabs"
        tabClassname="HelpMenu--body--Tab"
        activeClassname="HelpMenu--body--Tab--active"
      >
        <div index={1} label="Tools">
          <HelpMenuTools hasCalculator={!!calculatorType} />
        </div>
        <div index={2} label="Calculator" shouldBeHidden={!calculatorType}>
          <div style={{ margin: '32px 0px' }}>
            <iframe
              width="100%"
              height="331.9"
              src={calculatorVideos[calculatorType]}
              frameBorder="0"
              title="Calculator"
            />
          </div>
        </div>
      </Tabs>
    </div>
  </ConfirmDialog>
);

HelpMenu.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default HelpMenu;
